import React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown, Gem, Users } from 'lucide-react'
import ProfileLink from './ProfileLink'
import TeamTag from './TeamTag'

const formatAmount = (value) => Number(value || 0).toLocaleString()

export const BetAmount = ({ value, children, className = '', iconClassName = '' }) => (
  <span className={`inline-flex min-w-0 items-center gap-1 tabular-nums ${className}`}>
    <span className="min-w-0 break-all">{children ?? formatAmount(value)}</span>
    <Gem aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${iconClassName}`} />
  </span>
)

export const BetPlayerRow = ({ name, avatar, team, slug, amount, amountContent, amountClassName = 'text-accent-primary', iconClassName = '' }) => (
  <div className="flex min-w-0 items-center justify-between gap-3">
    {/* Имя и аватар ставки кликабельны: slug === null → игрок не кликабелен */}
    <ProfileLink slug={slug} className="flex min-w-0 items-center gap-2">
      {avatar ? <img src={avatar} alt="" loading="lazy" className="h-9 w-9 shrink-0 rounded-full border border-white/20 bg-surface object-cover" /> : <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-sm font-semibold text-white">{(name || 'И').slice(0, 1).toUpperCase()}</span>}
      {/* Банда — отдельной строкой над ником: в узкой колонке ставки
          «[Банда] Ник» одной строкой почти всегда обрезается. Без банды
          (`team: null`) строки нет вовсе — ник остаётся один */}
      <span className="min-w-0">
        {/* Обёртка — flex, а не блок: у инлайнового бейджа появляется
            строчный strut (пустое место под шрифт родителя), из-за него
            пара строк съезжает вниз относительно аватара */}
        {team?.name && <span className="flex min-w-0 leading-tight"><TeamTag team={team} className="mr-0 max-w-full text-[11px] leading-tight" /></span>}
        <span className="block truncate text-sm font-semibold leading-tight text-white">{name || 'Игрок'}</span>
      </span>
    </ProfileLink>
    <BetAmount value={amount} className={`shrink-0 text-sm font-semibold ${amountClassName}`} iconClassName={iconClassName}>{amountContent}</BetAmount>
  </div>
)

const GameBetsGroup = ({ id, label, multiplier, dotClassName = '', textClassName = '', color, total, playersCount, bets, isOpen, onToggle, renderBet, emptyText = 'Пока нет ставок', maxListHeight = false }) => (
  <div className="min-w-0 overflow-hidden rounded-xl border border-white/10 bg-surface">
    <button type="button" aria-expanded={isOpen} aria-controls={`${id}-bets`} aria-label={`${label}, множитель ${multiplier}${total > 0 ? `, всего ${formatAmount(total)} кристаллов` : ''}${playersCount > 0 ? `, ${playersCount} игроков` : ''}`} onClick={onToggle} className="flex min-h-[3.75rem] w-full min-w-0 items-center justify-between gap-2 px-3 py-3 text-left transition-colors hover:bg-white/[0.04] active:scale-[0.98]">
      <span className="flex shrink-0 items-center gap-2"><span aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 rounded-full ${dotClassName}`} style={color ? { backgroundColor: color } : undefined} /><span className={`text-lg font-bold ${textClassName}`} style={color ? { color } : undefined}>×{multiplier}</span></span>
      <span className="flex min-w-0 items-center gap-3">
        {total > 0 && <BetAmount value={total} className="whitespace-nowrap text-sm font-semibold text-white" />}
        {playersCount > 0 && <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs tabular-nums text-text-secondary"><Users aria-hidden="true" className="h-3.5 w-3.5" />{playersCount}</span>}
        <ChevronDown aria-hidden="true" className={`h-4 w-4 shrink-0 text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && <motion.div key={id} initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
        <div id={`${id}-bets`} className="border-t border-white/10 p-2">
          {bets.length === 0 ? <p className="py-3 text-center text-xs text-text-secondary">{emptyText}</p> : <div className={`space-y-2 ${maxListHeight ? 'max-h-[280px] overflow-y-auto pr-0.5' : ''}`}><AnimatePresence initial={false}>{bets.map(renderBet)}</AnimatePresence></div>}
        </div>
      </motion.div>}
    </AnimatePresence>
  </div>
)

export default GameBetsGroup
